// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

export type {
  SynonymMap,
  SearchResourceEncryptionKey,
  AzureActiveDirectoryApplicationCredentials,
  SearchIndexerDataIdentity,
  SearchIndexerDataIdentityUnion,
  SearchIndexerDataNoneIdentity,
  SearchIndexerDataUserAssignedIdentity,
  ListSynonymMapsResult,
  SearchIndex,
  SearchField,
  SearchFieldDataType,
  LexicalAnalyzerName,
  LexicalNormalizerName,
  VectorEncodingFormat,
  ScoringProfile,
  TextWeights,
  ScoringFunction,
  ScoringFunctionUnion,
  ScoringFunctionInterpolation,
  DistanceScoringFunction,
  DistanceScoringParameters,
  FreshnessScoringFunction,
  FreshnessScoringParameters,
  MagnitudeScoringFunction,
  MagnitudeScoringParameters,
  TagScoringFunction,
  TagScoringParameters,
  ScoringFunctionAggregation,
  CorsOptions,
  SearchSuggester,
  LexicalAnalyzer,
  LexicalAnalyzerUnion,
  CustomAnalyzer,
  LexicalTokenizerName,
  TokenFilterName,
  CharFilterName,
  PatternAnalyzer,
  RegexFlags,
  LuceneStandardAnalyzer,
  StopAnalyzer,
  LexicalTokenizer,
  LexicalTokenizerUnion,
  ClassicTokenizer,
  EdgeNGramTokenizer,
  TokenCharacterKind,
  KeywordTokenizer,
  KeywordTokenizerV2,
  MicrosoftLanguageTokenizer,
  MicrosoftTokenizerLanguage,
  MicrosoftLanguageStemmingTokenizer,
  MicrosoftStemmingTokenizerLanguage,
  NGramTokenizer,
  PathHierarchyTokenizerV2,
  PatternTokenizer,
  LuceneStandardTokenizer,
  LuceneStandardTokenizerV2,
  UaxUrlEmailTokenizer,
  TokenFilter,
  TokenFilterUnion,
  AsciiFoldingTokenFilter,
  CjkBigramTokenFilter,
  CjkBigramTokenFilterScripts,
  CommonGramTokenFilter,
  DictionaryDecompounderTokenFilter,
  EdgeNGramTokenFilter,
  EdgeNGramTokenFilterSide,
  EdgeNGramTokenFilterV2,
  ElisionTokenFilter,
  KeepTokenFilter,
  KeywordMarkerTokenFilter,
  LengthTokenFilter,
  LimitTokenFilter,
  NGramTokenFilter,
  NGramTokenFilterV2,
  PatternCaptureTokenFilter,
  PatternReplaceTokenFilter,
  PhoneticTokenFilter,
  PhoneticEncoder,
  ShingleTokenFilter,
  SnowballTokenFilter,
  SnowballTokenFilterLanguage,
  StemmerTokenFilter,
  StemmerTokenFilterLanguage,
  StemmerOverrideTokenFilter,
  StopwordsTokenFilter,
  StopwordsList,
  SynonymTokenFilter,
  TruncateTokenFilter,
  UniqueTokenFilter,
  WordDelimiterTokenFilter,
  CharFilter,
  CharFilterUnion,
  MappingCharFilter,
  PatternReplaceCharFilter,
  LexicalNormalizer,
  LexicalNormalizerUnion,
  CustomNormalizer,
  SimilarityAlgorithm,
  SimilarityAlgorithmUnion,
  ClassicSimilarity,
  BM25Similarity,
  SemanticSearch,
  SemanticConfiguration,
  SemanticPrioritizedFields,
  SemanticField,
  RankingOrder,
  VectorSearch,
  VectorSearchProfile,
  VectorSearchAlgorithmConfiguration,
  VectorSearchAlgorithmConfigurationUnion,
  VectorSearchAlgorithmKind,
  HnswAlgorithmConfiguration,
  HnswParameters,
  VectorSearchAlgorithmMetric,
  ExhaustiveKnnAlgorithmConfiguration,
  ExhaustiveKnnParameters,
  VectorSearchVectorizer,
  VectorSearchVectorizerUnion,
  VectorSearchVectorizerKind,
  AzureOpenAIVectorizer,
  AzureOpenAIVectorizerParameters,
  AzureOpenAIModelName,
  WebApiVectorizer,
  WebApiVectorizerParameters,
  AzureMachineLearningVectorizer,
  AzureMachineLearningParameters,
  AIFoundryModelCatalogName,
  VectorSearchCompression,
  VectorSearchCompressionUnion,
  RescoringOptions,
  VectorSearchCompressionRescoreStorageMethod,
  VectorSearchCompressionKind,
  ScalarQuantizationCompression,
  ScalarQuantizationParameters,
  VectorSearchCompressionTarget,
  BinaryQuantizationCompression,
  SearchIndexResponse,
  GetIndexStatisticsResult,
  AnalyzeTextOptions,
  AnalyzeResult,
  AnalyzedTokenInfo,
  SearchAlias,
  KnowledgeBase,
  KnowledgeSourceReference,
  KnowledgeBaseModel,
  KnowledgeBaseModelUnion,
  KnowledgeBaseModelKind,
  KnowledgeBaseAzureOpenAIModel,
  KnowledgeSource,
  KnowledgeSourceUnion,
  KnowledgeSourceKind,
  SearchIndexKnowledgeSource,
  SearchIndexKnowledgeSourceParameters,
  SearchIndexFieldReference,
  AzureBlobKnowledgeSource,
  AzureBlobKnowledgeSourceParameters,
  IndexingSchedule,
  KnowledgeSourceContentExtractionMode,
  CreatedResources,
  IndexedOneLakeKnowledgeSource,
  IndexedOneLakeKnowledgeSourceParameters,
  WebKnowledgeSource,
  WebKnowledgeSourceParameters,
  WebKnowledgeSourceDomains,
  WebKnowledgeSourceDomain,
  KnowledgeSourceSynchronizationStatus,
  SearchServiceStatistics,
  ServiceCounters,
  ResourceCounter,
  ServiceLimits,
  SearchIndexerDataSourceConnection,
  SearchIndexerDataSourceType,
  DataSourceCredentials,
  SearchIndexerDataContainer,
  DataChangeDetectionPolicy,
  DataChangeDetectionPolicyUnion,
  HighWaterMarkChangeDetectionPolicy,
  SqlIntegratedChangeTrackingPolicy,
  DataDeletionDetectionPolicy,
  DataDeletionDetectionPolicyUnion,
  SoftDeleteColumnDeletionDetectionPolicy,
  NativeBlobSoftDeleteDeletionDetectionPolicy,
  ListDataSourcesResult,
  SearchIndexer,
  IndexingParameters,
  IndexingParametersConfiguration,
  BlobIndexerParsingMode,
  MarkdownParsingSubmode,
  MarkdownHeaderDepth,
  BlobIndexerDataToExtract,
  BlobIndexerImageAction,
  BlobIndexerPDFTextRotationAlgorithm,
  IndexerExecutionEnvironment,
  FieldMapping,
  FieldMappingFunction,
  ListIndexersResult,
  SearchIndexerStatus,
  IndexerStatus,
  IndexerExecutionResult,
  IndexerExecutionStatus,
  SearchIndexerError,
  SearchIndexerWarning,
  SearchIndexerLimits,
  SearchIndexerSkillset,
  SearchIndexerSkill,
  SearchIndexerSkillUnion,
  InputFieldMappingEntry,
  OutputFieldMappingEntry,
  ConditionalSkill,
  KeyPhraseExtractionSkill,
  KeyPhraseExtractionSkillLanguage,
  OcrSkill,
  OcrSkillLanguage,
  OcrLineEnding,
  ImageAnalysisSkill,
  ImageAnalysisSkillLanguage,
  VisualFeature,
  ImageDetail,
  LanguageDetectionSkill,
  ShaperSkill,
  MergeSkill,
  SentimentSkillV3,
  SentimentSkillLanguage,
  EntityLinkingSkill,
  EntityRecognitionSkillV3,
  EntityCategory,
  EntityRecognitionSkillLanguage,
  PIIDetectionSkill,
  PIIDetectionSkillMaskingMode,
  SplitSkill,
  SplitSkillLanguage,
  TextSplitMode,
  CustomEntityLookupSkill,
  CustomEntityLookupSkillLanguage,
  CustomEntity,
  CustomEntityAlias,
  TextTranslationSkill,
  TextTranslationSkillLanguage,
  DocumentExtractionSkill,
  DocumentIntelligenceLayoutSkill,
  DocumentIntelligenceLayoutSkillOutputFormat,
  DocumentIntelligenceLayoutSkillOutputMode,
  DocumentIntelligenceLayoutSkillMarkdownHeaderDepth,
  DocumentIntelligenceLayoutSkillExtractionOptions,
  DocumentIntelligenceLayoutSkillChunkingProperties,
  DocumentIntelligenceLayoutSkillChunkingUnit,
  WebApiSkill,
  WebApiHttpHeaders,
  AzureOpenAIEmbeddingSkill,
  ContentUnderstandingSkill,
  ContentUnderstandingSkillExtractionOptions,
  ContentUnderstandingSkillChunkingProperties,
  ContentUnderstandingSkillChunkingUnit,
  ChatCompletionSkill,
  CommonModelParameters,
  ChatCompletionExtraParametersBehavior,
  ChatCompletionResponseFormat,
  ChatCompletionResponseFormatType,
  ChatCompletionSchemaProperties,
  ChatCompletionSchema,
  CognitiveServicesAccount,
  CognitiveServicesAccountUnion,
  DefaultCognitiveServicesAccount,
  CognitiveServicesAccountKey,
  AIServicesAccountKey,
  AIServicesAccountIdentity,
  SearchIndexerKnowledgeStore,
  SearchIndexerKnowledgeStoreProjection,
  SearchIndexerKnowledgeStoreTableProjectionSelector,
  SearchIndexerKnowledgeStoreObjectProjectionSelector,
  SearchIndexerKnowledgeStoreFileProjectionSelector,
  SearchIndexerIndexProjection,
  SearchIndexerIndexProjectionSelector,
  SearchIndexerIndexProjectionsParameters,
  IndexProjectionMode,
  SearchIndexerKnowledgeStoreProjectionSelector,
  SearchIndexerKnowledgeStoreBlobProjectionSelector,
  ListSkillsetsResult,
  IndexerResyncBody,
  IndexerResyncOption,
  DocumentKeysOrIds,
  SkillNames,
} from "./models.js";
export {
  KnownSearchFieldDataType,
  KnownLexicalAnalyzerName,
  KnownLexicalNormalizerName,
  KnownVectorEncodingFormat,
  KnownLexicalTokenizerName,
  KnownTokenFilterName,
  KnownCharFilterName,
  KnownRegexFlags,
  KnownRankingOrder,
  KnownVectorSearchAlgorithmKind,
  KnownVectorSearchAlgorithmMetric,
  KnownVectorSearchVectorizerKind,
  KnownAzureOpenAIModelName,
  KnownAIFoundryModelCatalogName,
  KnownVectorSearchCompressionRescoreStorageMethod,
  KnownVectorSearchCompressionKind,
  KnownVectorSearchCompressionTarget,
  KnownKnowledgeBaseModelKind,
  KnownKnowledgeSourceKind,
  KnownKnowledgeSourceContentExtractionMode,
  KnownKnowledgeSourceSynchronizationStatus,
  KnownSearchIndexerDataSourceType,
  KnownBlobIndexerParsingMode,
  KnownMarkdownParsingSubmode,
  KnownMarkdownHeaderDepth,
  KnownBlobIndexerDataToExtract,
  KnownBlobIndexerImageAction,
  KnownBlobIndexerPDFTextRotationAlgorithm,
  KnownIndexerExecutionEnvironment,
  KnownKeyPhraseExtractionSkillLanguage,
  KnownOcrSkillLanguage,
  KnownOcrLineEnding,
  KnownImageAnalysisSkillLanguage,
  KnownVisualFeature,
  KnownImageDetail,
  KnownSentimentSkillLanguage,
  KnownEntityCategory,
  KnownEntityRecognitionSkillLanguage,
  KnownPIIDetectionSkillMaskingMode,
  KnownSplitSkillLanguage,
  KnownTextSplitMode,
  KnownCustomEntityLookupSkillLanguage,
  KnownTextTranslationSkillLanguage,
  KnownDocumentIntelligenceLayoutSkillOutputFormat,
  KnownDocumentIntelligenceLayoutSkillOutputMode,
  KnownDocumentIntelligenceLayoutSkillMarkdownHeaderDepth,
  KnownDocumentIntelligenceLayoutSkillExtractionOptions,
  KnownDocumentIntelligenceLayoutSkillChunkingUnit,
  KnownContentUnderstandingSkillExtractionOptions,
  KnownContentUnderstandingSkillChunkingUnit,
  KnownChatCompletionExtraParametersBehavior,
  KnownChatCompletionResponseFormatType,
  KnownIndexProjectionMode,
  KnownIndexerResyncOption,
} from "./models.js";
